import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { Icon, Checkbox, Popup } from 'semantic-ui-react'
import { showProvider, showTrafficOnMap } from '../../actions/directionsActions'

import formatDuration from 'utils/date_time'
import { VALHALLA_OSM_URL } from 'utils/valhalla'
import { TRAFFIC_LEGEND, MAP_CONGESTION_LEGEND } from 'traffic'

const COLOR_HEX = {
  green: '#3ecf8e',
  yellow: '#fbbf24',
  orange: '#f97316',
  red: '#ff6b6b',
  unavailable: '#d946ef',
  updated: '#3d8bfd',
}

const getLength = (length, unit) => {
  const visibleLength = length * 1000
  const displayUnit = unit === 'km' ? 'km' : 'mi'
  return (visibleLength / 1000).toFixed(2) + ' ' + displayUnit
}

class Summary extends React.Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    results: PropTypes.object,
    inclineDeclineTotal: PropTypes.object,
    summary: PropTypes.object.isRequired,
    summary_dp: PropTypes.object,
    header: PropTypes.string.isRequired,
    idx: PropTypes.number.isRequired,
    provider: PropTypes.string,
    trafficEnabled: PropTypes.bool,
  }

  static defaultProps = {
    provider: VALHALLA_OSM_URL,
  }

  handleChange = (event, data) => {
    const { dispatch, idx } = this.props
    dispatch(showProvider(data.provider, data.checked, idx))
  }

  handleTrafficMapChange = (event, data) => {
    const { dispatch, idx, provider } = this.props
    dispatch(showTrafficOnMap(provider || VALHALLA_OSM_URL, data.checked, idx))
  }

  renderTrafficBlock(traffic) {
    if (!traffic) {
      return null
    }
    if (traffic.loading) {
      return (
        <div className="f6 pb2" style={{ color: '#9aa8bc' }}>
          Loading Mapbox traffic…
        </div>
      )
    }

    const totals = traffic.totals
    const failedLegs = (traffic.legs || []).filter((leg) => !leg.ok)
    const gray = { color: '#9aa8bc' }
    const hasDebugDetails =
      Boolean(totals) ||
      Boolean(traffic.uuid) ||
      totals?.shapeCoordinateCount != null

    return (
      <div className="pb2" style={{ fontSize: '0.85rem' }}>
        <div className="b mb1">Traffic (Mapbox)</div>
        {traffic.error && !traffic.ok ? (
          <div style={{ color: '#ff6b6b' }}>
            {traffic.error}
            {traffic.legs?.[0]?.coordinateCount != null && (
              <span> · coords {traffic.legs[0].coordinateCount}</span>
            )}
          </div>
        ) : null}
        {failedLegs.map((leg, i) => (
          <div key={`leg-err-${i}`} style={{ color: '#ff6b6b' }}>
            Leg {i + 1}: {leg.error || leg.code}
            {leg.coordinateCount != null
              ? ` (${leg.coordinateCount} densify coords)`
              : ''}
          </div>
        ))}
        {(traffic.legs || []).map((leg, legIndex) =>
          leg.ok && leg.trafficUnavailable ? (
            <div
              key={`leg-unavail-${legIndex}`}
              style={{ color: COLOR_HEX.unavailable }}
            >
              Leg {legIndex + 1}: traffic unavailable
              {leg.coveragePct != null ? ` (coverage ${leg.coveragePct}%)` : ''}
            </div>
          ) : null
        )}
        {totals && totals.traffic?.time != null && (
          <div
            className="flex flex-wrap"
            style={{
              gap: '0.75rem',
              alignItems: 'center',
            }}
          >
            <span
              style={{
                color: COLOR_HEX[totals.color] || 'inherit',
                background:
                  totals.color === 'red'
                    ? 'rgba(255, 107, 107, 0.15)'
                    : totals.color === 'orange'
                    ? 'rgba(249, 115, 22, 0.15)'
                    : totals.color === 'yellow'
                    ? 'rgba(251, 191, 36, 0.18)'
                    : totals.color === 'unavailable'
                    ? 'rgba(217, 70, 239, 0.12)'
                    : 'rgba(62, 207, 142, 0.15)',
                border: `1px solid ${COLOR_HEX[totals.color] || '#9aa8bc'}`,
                borderRadius: 4,
                padding: '0.15rem 0.4rem',
                fontWeight: 600,
              }}
            >
              {totals.situationLabel || 'ETA'}{' '}
              {formatDuration(totals.traffic.time)}
            </span>
          </div>
        )}
        {hasDebugDetails && (
          <details className="mt1">
            <summary
              style={{
                ...gray,
                cursor: 'pointer',
                fontSize: '0.78rem',
                userSelect: 'none',
              }}
            >
              Debug details
              {totals?.matchingFactorAvg != null
                ? ` · match ${totals.matchingFactorAvg}%`
                : ''}
              {totals?.coveragePct != null
                ? ` · coverage ${totals.coveragePct}%`
                : ''}
            </summary>
            <div
              className="mt1"
              style={{ ...gray, fontSize: '0.72rem', lineHeight: 1.45 }}
            >
              {totals?.shapeCoordinateCount != null && (
                <div className="mb1">
                  Route coords {totals.shapeCoordinateCount}
                  {Array.isArray(traffic.legs) &&
                    traffic.legs.map((leg, i) => (
                      <span key={`leg-coords-${i}`}>
                        {' · '}leg {i + 1}: {leg.shapeCoordinateCount ?? '—'}
                        {leg.coordinateCount != null
                          ? ` (match ${leg.coordinateCount})`
                          : ''}
                      </span>
                    ))}
                </div>
              )}
              {totals && (
                <>
                  <div>
                    Backend (baseline):{' '}
                    {formatDuration(totals.valhalla?.time || 0)}
                  </div>
                  <div>
                    Mapbox (no traffic / free-flow):{' '}
                    {totals.traffic?.freeFlowSec != null
                      ? formatDuration(totals.traffic.freeFlowSec)
                      : '—'}
                  </div>
                  <div>
                    Mapbox (with traffic):{' '}
                    {formatDuration(
                      totals.traffic?.mapboxTrafficSec ??
                        totals.traffic?.time ??
                        0
                    )}
                  </div>
                  <div>
                    hybrid: matched{' '}
                    {formatDuration(totals.traffic?.matchedTime || 0)} +
                    fallback {formatDuration(totals.traffic?.fallbackTime || 0)}
                    {totals.traffic?.trafficDelaySec > 0
                      ? ` · congestion delay ${formatDuration(
                          totals.traffic.trafficDelaySec
                        )}`
                      : ''}
                  </div>
                  <div>
                    distance{' '}
                    {(
                      (totals.traffic?.lengthMiles ??
                        totals.valhalla?.length) ||
                      0
                    ).toFixed(2)}{' '}
                    mi (baseline unchanged)
                  </div>
                </>
              )}
              {traffic.uuid && <div>uuid {traffic.uuid}</div>}
              {(totals?.legsDebug || []).map((row) => (
                <div key={`dbg-${row.index}`}>
                  leg {row.index + 1}:{' '}
                  {row.ok
                    ? row.unavailable
                      ? 'unavailable'
                      : `${row.situationKind || '—'} · cov ${
                          row.coveragePct
                        }% · mf ${row.matchingFactor}% · hyb ${formatDuration(
                          row.hybridTime || 0
                        )} (mb ${formatDuration(
                          row.mapboxDuration || 0
                        )} / base ${formatDuration(
                          row.baselineTime || 0
                        )}) · delay ${formatDuration(
                          row.trafficDelaySec || 0
                        )} · matched ${formatDuration(
                          row.matchedTime || 0
                        )} · fallback ${formatDuration(
                          row.fallbackTime || 0
                        )} · shape ${row.shapeCoords} densify ${
                          row.densifyCoords
                        }`
                    : row.error || 'failed'}
                </div>
              ))}
            </div>
          </details>
        )}
        <div
          className="flex flex-wrap mt1"
          style={{ gap: '0.5rem', color: '#9aa8bc', fontSize: '0.75rem' }}
        >
          <span style={{ width: '100%' }}>ETA traffic level:</span>
          {TRAFFIC_LEGEND.map((item) => (
            <span key={item.color} title={item.detail}>
              <span
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: item.hex,
                  marginRight: 4,
                }}
              />
              {item.label}
            </span>
          ))}
        </div>
        {totals?.mapColorMode === 'predicted' ? (
          <div
            className="mt1"
            style={{ color: '#9aa8bc', fontSize: '0.75rem' }}
          >
            Map congestion coloring is off for future / arrive-by departures
            (ETA still uses predicted traffic times).
          </div>
        ) : (
          <div
            className="flex flex-wrap mt1"
            style={{ gap: '0.5rem', color: '#9aa8bc', fontSize: '0.75rem' }}
          >
            <span style={{ width: '100%' }}>Map (current congestion):</span>
            {MAP_CONGESTION_LEGEND.map((item) => (
              <span key={item.color} title={item.detail}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: item.hex,
                    marginRight: 4,
                  }}
                />
                {item.label}
              </span>
            ))}
          </div>
        )}
      </div>
    )
  }

  render() {
    const {
      results,
      summary,
      summary_dp,
      inclineDeclineTotal,
      header,
      idx,
      provider,
      trafficEnabled,
    } = this.props

    const traffic =
      trafficEnabled &&
      results[provider || VALHALLA_OSM_URL]?.trafficByRouteIndex?.[idx]

    return (
      <React.Fragment>
        {summary ? (
          <React.Fragment>
            <div className="flex mb1">
              <span className="b">{header}</span>
              {summary.has_highway && (
                <div style={{ marginLeft: '1em' }}>
                  <Popup
                    content={'Highway'}
                    size={'tiny'}
                    trigger={
                      <div className={'flex'}>
                        <Icon
                          circular
                          name={'road'}
                          size="small"
                          style={{ marginRight: '-10px' }}
                        />
                        <div className={'dib pa1 f6'}></div>
                      </div>
                    }
                  />
                </div>
              )}
              {summary.has_ferry && (
                <div style={{ marginLeft: '1em' }}>
                  <Popup
                    content={'Ferry'}
                    size={'tiny'}
                    trigger={
                      <div className={'flex'}>
                        <Icon
                          circular
                          name={'ship'}
                          size="small"
                          style={{ marginRight: '-10px' }}
                        />
                        <div className={'dib pa1 f6'}></div>
                      </div>
                    }
                  />
                </div>
              )}
              {summary.has_toll && (
                <div style={{ marginLeft: '1em' }}>
                  <Popup
                    content={'Toll'}
                    size={'tiny'}
                    style={{ marginRight: '-10px' }}
                    trigger={
                      <div className={'flex'}>
                        <Icon circular name={'dollar'} size="small" />
                        <div className={'dib pa1 f6'}></div>
                      </div>
                    }
                  />
                </div>
              )}
            </div>
            {summary_dp && (
              <div className={'flex justify-between pb2 pointer'}>
                <div className={'dib v-mid pa1 b f6'}>
                  <Icon circular name={'signal'} size="small" />
                  {summary_dp.combined_score.toFixed(2)}
                  <Icon circular name={'shield'} size="small" />
                  {summary_dp.score.toFixed(2)}
                  <Icon circular name={'compass'} size="small" />
                  {summary_dp.method}
                </div>
              </div>
            )}
            <div className={'flex justify-between pb2 pointer'}>
              <div
                style={{
                  alignSelf: 'center',
                  flexBasis: '100px',
                }}
              >
                <Icon
                  circular
                  name={'arrows alternate horizontal'}
                  size={'small'}
                />
                <div className={'dib v-mid pa1 b f6'}>
                  {getLength(summary.length, summary.unit)}
                </div>
              </div>
              <div
                style={{
                  alignSelf: 'center',
                  flexGrow: 1,
                }}
              >
                <Icon circular name={'time'} size="small" />
                <div className={'dib v-mid pa1 b f6'}>
                  {formatDuration(summary.time)}
                </div>
              </div>
              <div
                style={{
                  alignSelf: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.35rem',
                  alignItems: 'flex-end',
                }}
              >
                <Checkbox
                  slider
                  label={'Map'}
                  checked={
                    results[this.props.provider || VALHALLA_OSM_URL]?.show?.[
                      idx
                    ] ?? false
                  }
                  provider={this.props.provider || VALHALLA_OSM_URL}
                  onChange={(event, data) => this.handleChange(event, data)}
                />
                {traffic && traffic.ok && (
                  <Checkbox
                    slider
                    label={'Traffic map'}
                    checked={
                      results[this.props.provider || VALHALLA_OSM_URL]
                        ?.showTraffic?.[idx] ?? false
                    }
                    onChange={(event, data) =>
                      this.handleTrafficMapChange(event, data)
                    }
                  />
                )}
              </div>
            </div>
            {this.renderTrafficBlock(traffic)}
            {inclineDeclineTotal && (
              <div className={'flex pb3 pointer'}>
                <div
                  style={{
                    alignSelf: 'center',
                    marginRight: '1em',
                  }}
                >
                  <Popup
                    content={'Total Incline'}
                    size={'tiny'}
                    trigger={
                      <div>
                        <Icon circular name={'arrow up'} size={'small'} />
                        <div className={'dib v-mid pa1 b f6'}>
                          {`${inclineDeclineTotal.inclineTotal} m`}
                        </div>
                      </div>
                    }
                  />
                </div>
                <div
                  style={{
                    alignSelf: 'center',
                    flexBasis: '100px',
                  }}
                >
                  <Popup
                    content={'Total Decline'}
                    size={'tiny'}
                    trigger={
                      <div>
                        <Icon circular name={'arrow down'} size={'small'} />
                        <div className={'dib v-mid pa1 b f6'}>
                          {`${inclineDeclineTotal.declineTotal} m`}
                        </div>
                      </div>
                    }
                  />
                </div>
              </div>
            )}
          </React.Fragment>
        ) : (
          <div>No route found</div>
        )}
      </React.Fragment>
    )
  }
}

const mapStateToProps = (state) => {
  const { results, inclineDeclineTotal } = state.directions
  const { trafficEnabled } = state.common
  return {
    results,
    inclineDeclineTotal,
    trafficEnabled,
  }
}

export default connect(mapStateToProps)(Summary)
